import React, { memo, useCallback, useState } from "react";

import { FormCollapse } from "components/Form";
import { Column, Row } from "components/Layout";
import { SubtleBadge } from "components/Badge";
import { SlimButton } from "components/Button";
import Grid from "@material-ui/core/Grid";
import { useBoolean } from "hooks";
import { getBusinessById, getBusinessesError } from "selectors";
import { useDispatch, useSelector } from "react-redux";
import {
  closeConfirmModal,
  MergeBusinessesAction,
  openConfirmModal,
} from "actions";
import { BUSINESS } from "constants/actionTypes";
import { ResetError } from "actions/lib";
import { MergedBusinessesTable } from "./MergedBusinssesTable";
import { MergeBusinessDialog } from "./MergeBusinessForm";

const header = (
  <Row>
    <SubtleBadge>Merged Businesses</SubtleBadge>
  </Row>
);

interface MergedBusinessesComponentProps {
  businessId: string;
  isOpen: boolean;
  onToggle: () => void;
}

const MergedBusinessesComponent: React.FC<MergedBusinessesComponentProps> = ({
  businessId,
  isOpen,
  onToggle,
}) => {
  const [sourceBusinessId, setSourceBusinessId] = useState<string>("");
  const [isCreateOpen, openCreate, closeCreate] = useBoolean(false);
  const targetBusiness: Business = useSelector(getBusinessById(businessId));
  const sourceBusiness: Business = useSelector(
    getBusinessById(sourceBusinessId),
  );

  const dispatch = useDispatch();

  const errorMessage = useSelector(getBusinessesError);

  const submitMergeBusiness = useCallback(() => {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message:
          `Are you sure you want to merge "${sourceBusiness.name}" into "${targetBusiness.name}"?\n\n` +
          `NOTE: "${sourceBusiness.name}" will be removed from view.`,
        actions: [
          {
            label: "No",
            secondary: true,
            onClick: () => {
              dispatch(closeConfirmModal());
            },
          },
          {
            label: "Yes",
            onClick: () => {
              dispatch(
                MergeBusinessesAction.create(
                  {},
                  {
                    targetBusinessId: targetBusiness.id,
                    sourceBusinessId: sourceBusiness.id,
                    suppressToast: false,
                    suppressErrorToast: false,
                    suppressSuccessToast: false,
                    changeReason: "Submitted from New Merge Event Modal",
                    saleyardName: null,
                  },
                ),
              );
              dispatch(closeConfirmModal());
            },
          },
        ],
      }),
    );
  }, [dispatch, sourceBusiness, targetBusiness]);

  const handleBusinessChange = (newSourceBusinessId: string | null) => {
    setSourceBusinessId(newSourceBusinessId);
    dispatch(ResetError(BUSINESS));
  };

  const onOpenMergeBusinessDialog = () => {
    // first, clear any errors from previous attempts
    dispatch(ResetError(BUSINESS));
    openCreate();
  };

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <Grid
        item
        xs={12}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Column />
        <Column>
          <SlimButton type="button" onClick={onOpenMergeBusinessDialog}>
            Merge Businesses
          </SlimButton>
        </Column>
      </Grid>
      <MergedBusinessesTable businessId={businessId} />
      {isCreateOpen && (
        <MergeBusinessDialog
          isOpen={isCreateOpen}
          handleClose={closeCreate}
          readOnly={false}
          handleSubmit={submitMergeBusiness}
          targetBusiness={targetBusiness}
          onBusinessChange={handleBusinessChange}
          errorMessage={errorMessage}
        />
      )}
    </FormCollapse>
  );
};

export default memo(MergedBusinessesComponent);
